import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Row, Col, Badge, InputGroup, Spinner, Alert, Card } from 'react-bootstrap';
import { FaHeart, FaComment, FaEye, FaPlus, FaEdit, FaTrash, FaSearch, FaCheck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';
import { useAuth } from '../../../contexts/AuthContext';
import { encodeForumId } from '../../../utils/urlEncryption';

const OrgForumList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [forums, setForums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingForum, setEditingForum] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [forumToDelete, setForumToDelete] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    attachment: null
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchForums();
  }, [filterStatus]);

  const fetchForums = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.forums.getAll({ filter: filterStatus });
      
      if (response.data.success) {
        const forums = response.data.data.forums || [];
        console.log('Fetched forums:', forums);
        
        // Debug image URLs
        forums.forEach(forum => {
          if (forum.attachment) {
            console.log('Forum attachment:', forum.attachment);
            console.log('Full image URL:', getAssetUrl(forum.attachment));
          }
        });
        
        setForums(forums);
      } else {
        setError(response.data.message || 'Failed to fetch forums');
      }
    } catch (error) {
      console.error('Error fetching forums:', error);
      setError('Failed to fetch forums. Please try again.');
      toast.error('Failed to fetch forums');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredForums = forums.filter(forum => {
    const matchesSearch = forum.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         forum.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Apply filter based on filterStatus
    let matchesFilter = true;
    if (filterStatus === 'organization') {
      // Show only forums created by the current org admin
      matchesFilter = forum.created_by === user.id;
    } else if (filterStatus === 'global') {
      // Show only global forums (org_id is null)
      matchesFilter = !forum.org_id;
    } else if (filterStatus === 'deleted') {
      // Show only deleted forums
      matchesFilter = parseInt(forum.deleted) === 1;
    }
    // For 'all' filter, matchesFilter remains true
    
    return matchesSearch && matchesFilter;
  });

  const handleCreateForum = () => {
    setEditingForum(null);
    setFormData({
      title: '',
      description: '',
      attachment: null
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditForum = (forum) => {
    setEditingForum(forum);
    setFormData({
      title: forum.title,
      description: forum.description || '',
      attachment: null
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDeleteForum = (forum) => {
    setForumToDelete(forum);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!forumToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await api.forums.delete(forumToDelete.id);
      
      if (response.data.success) {
        toast.success('Forum deleted successfully');
        setShowDeleteModal(false);
        setForumToDelete(null);
        await fetchForums();
      } else {
        toast.error(response.data.message || 'Failed to delete forum');
      }
    } catch (error) {
      console.error('Error deleting forum:', error);
      toast.error('Failed to delete forum');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestoreForum = async (forum) => {
    try {
      setRestoreLoading(true);
      const response = await api.forums.restore(forum.id);
      
      if (response.data.success) {
        toast.success('Forum restored successfully');
        await fetchForums();
      } else {
        toast.error(response.data.message || 'Failed to restore forum');
      }
    } catch (error) {
      console.error('Error restoring forum:', error);
      toast.error('Failed to restore forum');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setModalLoading(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('org_id', user.organization_id); // Include organization ID
      if (formData.attachment) {
        formDataToSend.append('attachment', formData.attachment);
      }

      let response;
      if (editingForum) {
        response = await api.forums.update(editingForum.id, formDataToSend);
      } else {
        response = await api.forums.create(formDataToSend);
      }
      
      if (response.data.success) {
        toast.success(editingForum ? 'Forum updated successfully' : 'Forum created successfully');
        setShowModal(false);
        setFormData({ title: '', description: '', attachment: null });
        setFormErrors({});
        setEditingForum(null);
        
        // Refresh forums list to show the new/updated forum
        await fetchForums();
      } else {
        toast.error(response.data.message || 'Failed to save forum');
      }
    } catch (error) {
      console.error('Error saving forum:', error);
      toast.error('Failed to save forum');
    } finally {
      setModalLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, attachment: file }));
    }
  };

  const canEditForum = (forum) => {
    return user.role === 2 && forum.created_by === user.id;
  };

  const canDeleteForum = (forum) => {
    return user.role === 2 && forum.created_by === user.id;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
        <div className="flex justify-center items-center" style={{ height: '400px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
        <div className="mb-3 p-3 rounded bg-red-50 border border-red-100 text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-4">
        <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Forum Discussions
        </h5>
      </div>

      {/* Filters and Search */}
      <Row className="mb-4 g-2 sm:g-3">
        <Col xs={12} md={6} className="mb-2 mb-md-0">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search forums..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </Col>
        <Col xs={12} sm={6} md={3} className="mb-2 mb-md-0">
          <Form.Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
          >
            <option value="all">All Forums</option>
            <option value="global">Global Forums</option>
            <option value="organization">My Forums</option>
            <option value="deleted">Deleted Forums</option>
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleCreateForum}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            <FaPlus className="w-4 h-4" />
            <span className="whitespace-nowrap">Create Forum</span>
          </Button>
        </Col>
      </Row>

      {/* Forums Cards */}
      {filteredForums.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl shadow-xl">
          <FaComment className="w-24 h-24 mx-auto text-purple-300 mb-4" />
          <p className="text-xl font-semibold text-purple-700">
            {searchTerm || filterStatus !== 'all' ? 'No forums found' : 'No forums yet'}
          </p>
          <p className="text-gray-600 mt-2">
            {searchTerm || filterStatus !== 'all'
              ? 'Try changing your search or filter'
              : 'Click "Create Forum" to create the first one'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
          {filteredForums.map(forum => (
            <div
              key={forum.id}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ${parseInt(forum.deleted) === 1 ? 'opacity-75' : ''}`}
            >
              {/* Image Section */}
              <div className="relative h-48 bg-gradient-to-br from-purple-100 to-pink-100">
                {forum.attachment ? (
                  <img
                    src={getAssetUrl(forum.attachment)}
                    alt={forum.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-purple-600">
                    <FaComment className="w-16 h-16 mb-3" />
                    <span className="font-semibold">No Image</span>
                  </div>
                )}

                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold text-white shadow-md ${forum.org_id ? 'bg-blue-600' : 'bg-green-600'}`}>
                    {forum.org_id ? 'Organization' : 'Global'}
                  </span>
                  {parseInt(forum.deleted) === 1 && (
                    <span className="px-3 py-1 rounded-full text-xs font-bold text-white shadow-md bg-red-600">
                      Deleted
                    </span>
                  )}
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4">
                <h3 className="font-bold text-lg text-gray-800 line-clamp-2 mb-2">{forum.title}</h3>
                <p className="text-gray-600 text-sm line-clamp-3 mb-4">{forum.description || 'No description'}</p>

                <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <span>by <strong>{forum.creator?.first_name} {forum.creator?.last_name}</strong></span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>{formatDate(forum.createdAt)}</span>
                  </div>
                </div>

                <div className="flex gap-4 text-sm mb-4">
                  <span className="flex items-center gap-1 text-red-600"><FaHeart /> {forum.likes_count || 0}</span>
                  <span className="flex items-center gap-1 text-blue-600"><FaComment /> {forum.comments_count || 0}</span>
                  <span className="flex items-center gap-1 text-gray-600"><FaEye /> {forum.views_count || 0}</span>
                </div>

                <div className="d-flex gap-1 justify-content-center">
                  <Button
                    bsPrefix="custom-hamburger"
                    onClick={() => navigate(`/organization/forums/${encodeForumId(forum.id)}`)}
                    title="View Forum"
                    className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                  >
                    <FaEye className="w-5 h-5" />
                  </Button>

                  {parseInt(forum.deleted) === 1 ? (
                    <Button
                      bsPrefix="custom-hamburger"
                      onClick={() => handleRestoreForum(forum)}
                      disabled={restoreLoading}
                      title="Restore Forum"
                      className="border-green-300 text-green-600 hover:bg-green-50 rounded p-1"
                    >
                      <FaCheck className="w-5 h-5" />
                    </Button>
                  ) : (
                    <>
                      {canEditForum(forum) && (
                        <Button
                          bsPrefix="custom-hamburger"
                          onClick={() => handleEditForum(forum)}
                          title="Edit Forum"
                          className="border-purple-300 text-purple-600 hover:bg-purple-100 rounded p-1"
                        >
                          <FaEdit className="w-5 h-5" />
                        </Button>
                      )}
                      {canDeleteForum(forum) && (
                        <Button
                          bsPrefix="custom-hamburger"
                          onClick={() => handleDeleteForum(forum)}
                          title="Delete Forum"
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                        >
                          <FaTrash className="w-5 h-5" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {editingForum ? 'Edit Forum' : 'Create New Forum'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg border ${formErrors.title ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
              />
              {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className={`w-full px-3 py-2 rounded-lg border ${formErrors.description ? 'border-red-500' : 'border-purple-200'} focus:ring-2 focus:ring-purple-500 outline-none text-sm`}
              />
              {formErrors.description && <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>}
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Attachment (Optional)</label>
              <input
                type="file"
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx"
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Supported formats: Images, PDF, DOC, DOCX (Max 5MB)</p>
            </div>
          </Modal.Body>
          <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
            <Row className="w-100 g-3">
              <Col md={6}>
                <Button bsPrefix="custom-hamburger" onClick={() => setShowModal(false)} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                  Cancel
                </Button>
              </Col>
              <Col md={6}>
                <Button
                  bsPrefix="custom-hamburger"
                  type="submit"
                  disabled={modalLoading}
                  className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                >
                  {modalLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    editingForum ? 'Update' : 'Create'
                  )}
                </Button>
              </Col>
            </Row>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5 text-center py-10">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Forum</h5>
          <p className="text-gray-600">
            Are you sure you want to delete the forum <strong>"{forumToDelete?.title}"</strong>? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button bsPrefix="custom-hamburger" onClick={() => setShowDeleteModal(false)} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgForumList;
